import React, { useEffect } from "react";
import { showError } from "../../../../utils/toast.ts";
import { Card } from "../../../shared/card/card.tsx";
import { EmptyStateMessage } from "../../../shared/empty-state-message/empty-state-message.tsx";
import { Spinner } from "../../../ui/spinner/spinner.tsx";
import { useProbationQuestions } from "../hooks/use-probation-questions.ts";
import { QuestionRenderer } from "./index.tsx";
import type { ProbationQuestion, SectionType } from "../schemas/index.ts";
import { isProbationSectionPending } from "../utils/probation-submission-status.ts";
import { sqeManagerRatingPercentage } from "../utils/sqe-manager-rating.ts";
import styles from "./probation-readonly-panel.module.css";

export interface ProbationReadOnlyPanelProps {
  cycleId: number;
  sectionType: SectionType;
}

function parseAnswerValue(question: ProbationQuestion): string | string[] {
  const raw = question.answer ?? "";

  if (question.questionType === "CHECKBOX") {
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  return raw;
}

function createReadOnlyField(value: string | string[], name: string) {
  return {
    name,
    state: { value, meta: { errors: [] as { message?: string }[] } },
    handleChange: () => {},
    handleBlur: () => {},
  };
}

export function ProbationReadOnlyPanel({ cycleId, sectionType }: ProbationReadOnlyPanelProps) {
  const { questions, loading, error } = useProbationQuestions({ cycleId, sectionType });

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  if (loading) {
    return <Spinner />;
  }

  if (isProbationSectionPending(questions)) {
    return (
      <div className={styles.pendingWrap}>
        <EmptyStateMessage message="Submission Pending" className={styles.pendingMessage} />
      </div>
    );
  }

  const readOnlyQuestions = questions.map((q) => ({ ...q, editable: false }));

  return (
    <div className={styles.root}>
      <Card className={styles.formCard}>
        <div className={styles.questions}>
          {readOnlyQuestions.map((question, index) => {
            const field = createReadOnlyField(
              parseAnswerValue(question),
              `readonly-${question.reviewId}`,
            );

            return (
              <React.Fragment key={question.reviewId}>
                <QuestionRenderer question={question} field={field} index={index + 1} />
                {sectionType === "SQE_MANAGER" && question.textAnswers ? (
                  <div>
                    <strong>Comments</strong>
                    <p>{question.textAnswers}</p>
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
        {sectionType === "SQE_MANAGER" ? (
          <div className={styles.ratingSummary}>
            <span>Overall Rating</span>
            <strong>{sqeManagerRatingPercentage(questions, questions.map((question) => ({
              reviewId: question.reviewId,
              answer: question.answer ?? "",
            })))}%</strong>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
